import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import theme from "theme";
import CheckAll from "routes/lenses/lens/files/CheckAll";
import { forwardRef, useState } from "react";

type PublishInfo = {
  clientId: number;
  clientName: string;
  lensId: number;
  lensName: string;
  errors: string[];
};

interface PublishModalProps {
  open: boolean;
  error: string;
  dryrun: boolean;
  isDisabled: boolean;
  updateInfo: PublishInfo[];
  close: () => void;
  publish: (onlyPublishLensIds: number[]) => Promise<void>;
  header: string;
  text: React.ReactNode;
}

export const PublishModal = forwardRef<HTMLDivElement, Omit<PublishModalProps, "ref">>(
  ({ open, error, dryrun, isDisabled, updateInfo, close, publish, header, text }, ref) => {
    const [selectedLensIds, setSelectedLensIds] = useState<number[]>([]);
    const selectedLensErrors = selectedLensIds.some((id) => {
      return updateInfo.some((p) => p.lensId === id && p.errors.length > 0);
    });
    const hasErrors = Boolean(error) || selectedLensErrors;
    const allLensIds = updateInfo.map((p) => p.lensId);

    const toggleSelect = (info: PublishInfo) => {
      if (selectedLensIds.find((lensId) => lensId === info.lensId) !== undefined) {
        setSelectedLensIds(selectedLensIds.filter((lensId) => lensId !== info.lensId));
      } else {
        setSelectedLensIds([...selectedLensIds, info.lensId]);
      }
    };
    return (
      <Dialog open={open} onClose={close} fullWidth maxWidth="md" ref={ref}>
        <DialogTitle>{header}</DialogTitle>
        <DialogContent>
          {error.length > 0 ? (
            <TableRow sx={{ color: theme.palette.error.main }}>{error}</TableRow>
          ) : (
            <>
              <p className="text-xs">{text}</p>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <CheckAll<number>
                        selectedItems={selectedLensIds}
                        items={allLensIds}
                        setItems={setSelectedLensIds}
                        compareFn={(a, b) => a === b}
                      />
                    </TableCell>
                    <TableCell>Client Id</TableCell>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Lens Id</TableCell>
                    <TableCell>Lens Name</TableCell>
                    <TableCell>Info</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {updateInfo.map((info, index) => (
                    <TableRow key={index} onClick={() => toggleSelect(info)}>
                      <TableCell>
                        <Checkbox
                          checked={!!selectedLensIds.find((lensId) => lensId === info.lensId)}
                          onChange={() => toggleSelect(info)}
                          size="small"
                          color="secondary"
                        />
                      </TableCell>
                      <TableCell>{info.clientId}</TableCell>
                      <TableCell>{info.clientName}</TableCell>
                      <TableCell>{info.lensId}</TableCell>
                      <TableCell>{info.lensName}</TableCell>
                      <TableCell>
                        {info.errors.length === 0 ? (
                          dryrun ? (
                            <p style={{ color: theme.palette.success.main }}>No Issues Detected</p>
                          ) : (
                            <p style={{ color: theme.palette.success.main }}>Published</p>
                          )
                        ) : (
                          <p style={{ color: theme.palette.error.main, whiteSpace: "pre" }}>
                            {info.errors.join("\r\n")}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Close</Button>
          {dryrun && (
            <Button
              variant="contained"
              onClick={() => publish(selectedLensIds)}
              disabled={hasErrors || isDisabled || selectedLensIds.length === 0}>
              Publish
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);
PublishModal.displayName = "PublishModal";
